/**
 * Wizard helpers — step ordering + URL search shape.
 *
 * 5-step nabídka creation flow (zákazník → vozidlo → financování → sleva →
 * souhrn). Each step persists into
 * the same `/v1/ojeta-vozidla/nabidky/{id}` record once an id is allocated by
 * step 1; the final souhrn step redirects to the detail page.
 *
 * All three ojetá wizards are create-only — editing an existing record
 * happens through the per-section modals in `EditModals.tsx` on the detail
 * pages, so nothing deep-links into a wizard with an existing id + step.
 */

import { coerceInt, coercePositiveInt, isStepId } from '@/lib/wizard-search';

export type WizardStepId = 'zakaznik' | 'vozidlo' | 'financovani' | 'sleva' | 'souhrn';

export const STEP_ORDER: WizardStepId[] = [
  'zakaznik',
  'vozidlo',
  'financovani',
  'sleva',
  'souhrn',
];

export const STEP_TITLES: Record<WizardStepId, string> = {
  zakaznik: 'Zákazník',
  vozidlo: 'Vozidlo',
  financovani: 'Financování',
  sleva: 'Sleva',
  souhrn: 'Souhrn',
};

export function isWizardStep(s: unknown): s is WizardStepId {
  return isStepId(STEP_ORDER, s);
}

export interface NabidkaWizardSearch {
  id?: number;
  step?: WizardStepId;
  /** Seed a brand-new offer from this used vehicle (create-from-vehicle). */
  vehicleId?: number;
  /** A customer just created via the "Nový zákazník" round-trip, to preselect. */
  id_zakaznika?: number;
}

export function parseNabidkaWizardSearch(
  input: Record<string, unknown>,
): NabidkaWizardSearch {
  const out: NabidkaWizardSearch = {};
  const id = coercePositiveInt(input.id);
  if (id != null) out.id = id;
  const s = input.step;
  if (isWizardStep(s)) out.step = s;
  const vehicleId = coercePositiveInt(input.vehicleId);
  if (vehicleId != null) out.vehicleId = vehicleId;
  const zak = coercePositiveInt(input.id_zakaznika);
  if (zak != null) out.id_zakaznika = zak;
  return out;
}

export function nabidkaCode(id: number): string {
  return String(id).padStart(6, '0');
}

export function smlouvaCode(id: number): string {
  return String(id).padStart(6, '0');
}

export interface SmlouvaWizardSearch {
  id?: number;
  step?: WizardStepId;
  /** Round-trip param from the "Nový zákazník" form (`?id_zakaznika=N`). */
  id_zakaznika?: number;
}

export function parseSmlouvaWizardSearch(
  input: Record<string, unknown>,
): SmlouvaWizardSearch {
  const out: SmlouvaWizardSearch = {};
  const id = coerceInt(input.id);
  if (id != null) out.id = id;
  const s = input.step;
  if (isWizardStep(s)) out.step = s;
  const zak = coercePositiveInt(input.id_zakaznika);
  if (zak != null) out.id_zakaznika = zak;
  return out;
}

// ─── Výkup wizard (Phase 7.5) ──────────────────────────────────────────────
//
// 4-step flow distinct from the nabídka/smlouva 5-step pattern. First step
// allocates the contract via POST /vykup (the VIN-search + vehicle form is
// one atomic create), then customer / financing / souhrn follow.

export type VykupStepId = 'vozidlo' | 'zakaznik' | 'financovani' | 'souhrn';

export const VYKUP_STEP_ORDER: VykupStepId[] = [
  'vozidlo',
  'zakaznik',
  'financovani',
  'souhrn',
];

export const VYKUP_STEP_TITLES: Record<VykupStepId, string> = {
  vozidlo: 'Vozidlo',
  zakaznik: 'Zákazník',
  financovani: 'Financování',
  souhrn: 'Souhrn',
};

function isVykupStep(s: unknown): s is VykupStepId {
  return isStepId(VYKUP_STEP_ORDER, s);
}

export interface VykupWizardSearch {
  id?: number;
  step?: VykupStepId;
  /** Round-trip param from the "Nový zákazník" form (`?id_zakaznika=N`). */
  id_zakaznika?: number;
}

export function parseVykupWizardSearch(
  input: Record<string, unknown>,
): VykupWizardSearch {
  const out: VykupWizardSearch = {};
  const id = coerceInt(input.id);
  if (id != null) out.id = id;
  const s = input.step;
  if (isVykupStep(s)) out.step = s;
  const zak = coercePositiveInt(input.id_zakaznika);
  if (zak != null) out.id_zakaznika = zak;
  return out;
}

export function vykupCode(id: number): string {
  return String(id).padStart(6, '0');
}
